import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useReducer,
  memo
} from 'react'
import { useTranslation } from 'react-i18next'
import { useTopToolbar } from '../../context/TopToolbarContext'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  FaChevronLeft,
  FaChevronRight,
  FaSearch,
  FaPlus,
  FaCalendarAlt
} from 'react-icons/fa'
import { getProductsInWarehouse } from '../../services/Inventory'
import { getCustomers } from '../../services/Order'
import {
  getAllDrafts,
  getDraftById,
  createDraftFromQuery,
  allocateProducts,
  getCandidates,
  createDraftFromSelection,
  updateDraftInput
} from '../../services/Draft'
import History from './History'
import NewNiwari from './NewNiwari'
import '../Schedule/Schedule.css'
import '../../components/Table/Table.css'
import historyIcon from '../../assets/history.png'
import newIcon from '../../assets/new.png'

// ─── Constants ────────────────────────────────────────────────────────────────

const coverageData = []
const orderedDetails = []
const scheduleData = []

// ─── Pure helpers (defined outside component — no recreation on render) ───────

const getStatus = (ordered, scheduled) => {
  const percentage = ordered ? (scheduled / ordered) * 100 : 0
  if (percentage < 100)
    return { text: 'Shortage', className: 'status-shortage' }
  if (percentage > 100) return { text: 'Excess', className: 'status-excess' }
  return { text: 'Sufficient', className: 'status-sufficient' }
}

/**
 * Shared draft mapping logic — was copy-pasted 3× in the original.
 * Now a single pure function called wherever needed.
 */
const mapDraft = (d) => {
  let deliveryDate = d.delivery_date || d.date
  if (!deliveryDate && d.input_data) {
    try {
      const parsed =
        typeof d.input_data === 'string'
          ? JSON.parse(d.input_data)
          : d.input_data
      if (parsed?.delivery_date) deliveryDate = parsed.delivery_date
    } catch (_) {}
  }
  return {
    id: d.id || d._id,
    name: d.name || `Draft ${d.id}`,
    date:
      deliveryDate ||
      (d.created_at
        ? d.created_at.slice(0, 10)
        : new Date().toISOString().slice(0, 10)),
    status: d.status || 'draft',
    draftData: d.data || null,
    ...d
  }
}

const buildDraftDataFromDetail = (draftDetail) => {
  let inputData = {}
  let outputData = {}
  try {
    inputData =
      typeof draftDetail.input_data === 'string'
        ? JSON.parse(draftDetail.input_data)
        : draftDetail.input_data
  } catch (e) {
    console.error('Error parsing input_data', e)
  }
  try {
    outputData =
      typeof draftDetail.output_data === 'string'
        ? JSON.parse(draftDetail.output_data)
        : draftDetail.output_data
  } catch (e) {
    console.error('Error parsing output_data', e)
  }

  const rawInventory =
    inputData?.data?.inventory ||
    inputData.inventory ||
    inputData.inventoryList ||
    []
  const rawCustomers =
    inputData?.data?.customers ||
    inputData.customers ||
    inputData.orderList ||
    []
  const orderScheduledList = outputData?.result?.orderScheduledList || []

  const newLevel2Inventory = rawInventory.map((item, idx) => {
    const mCode = item.MEISEI_HINMEI_CD || item.meisei_hinmei_cd
    const qty = item.QUANTITY || item.quantity
    const delDate = item.DELIVERY_DATE || item.delivery_date
    const price =
      item.PRICE ||
      item.price ||
      Math.floor(Math.random() * (400 - 300 + 1) + 300)
    return {
      id: idx + 1,
      originalId: item.ID !== undefined ? item.ID : idx + 1,
      parentId: `parent-${mCode}-${item.GRADE || item.grade}-${item.SIZE || item.size}-${item.PRODUCTION || item.production}`,
      MEISEI_HINMEI_CD: mCode,
      LABEL: `${mCode}-${idx + 1}`,
      QUANTITY: qty,
      DELIVERY_DATE: delDate,
      PRICE: price
    }
  })

  const groupedInv = {}
  newLevel2Inventory.forEach((l2) => {
    if (!groupedInv[l2.parentId]) {
      const rawItem = rawInventory.find(
        (r) =>
          (r.MEISEI_HINMEI_CD || r.meisei_hinmei_cd) === l2.MEISEI_HINMEI_CD
      )
      groupedInv[l2.parentId] = {
        id: l2.parentId,
        MEISEI_HINMEI_CD: l2.MEISEI_HINMEI_CD,
        GRADE: rawItem ? rawItem.GRADE || rawItem.grade : '',
        SIZE: rawItem ? rawItem.SIZE || rawItem.size : '',
        PRODUCTION: rawItem ? rawItem.PRODUCTION || rawItem.production : '',
        QUANTITY: 0
      }
    }
    groupedInv[l2.parentId].QUANTITY += l2.QUANTITY
  })
  const newDetailsInventory = Object.values(groupedInv)

  const newAllocationResults = rawCustomers.map((c, i) => ({
    _uiId: `cust-${i}`,
    CUSTOMER_CODE: (c.customer_code || c.CUSTOMER_CODE || '').trim(),
    BRANCH_CODE: (c.branch_code || c.BRANCH_CODE || '').trim(),
    MEISEI_HINMEI_CD: c.MEISEI_HINMEI_CD || c.meisei_hinmei_cd,
    GRADE: Array.isArray(c.GRADE || c.grade)
      ? (c.GRADE || c.grade)[0]
      : c.GRADE || c.grade,
    SIZE: Array.isArray(c.SIZE || c.size)
      ? (c.SIZE || c.size)[0]
      : c.SIZE || c.size,
    PRODUCTION: Array.isArray(c.PRODUCTION || c.production)
      ? (c.PRODUCTION || c.production)[0]
      : c.PRODUCTION || c.production,
    ORDER_QUANTITY: c.ORDER_QUANTITY || c.order_quantity,
    PRIORITY: c.PRIORITY || c.priority || 1,
    SATISFACTION_PERCENT:
      c.SATISFACTION_PERCENT || c.satisfaction_percent || 100,
    AGENTLIST: c.AGENTLIST || c.agentlist || [],
    UNIT_PRICE:
      c.UNIT_PRICE ||
      c.unit_price ||
      c.PRICE ||
      c.price ||
      Math.floor(Math.random() * (400 - 300 + 1) + 300)
  }))

  const newGroupedById = {}
  newDetailsInventory.forEach((det) => {
    newGroupedById[det.id] = newAllocationResults.filter(
      (r) => String(r.MEISEI_HINMEI_CD) === String(det.MEISEI_HINMEI_CD)
    )
  })

  const finalMatrix = {}
  orderScheduledList.forEach((order) => {
    const custCode = (order.CUSTOMER_CODE || '').trim()
    const branchCode = (order.BRANCH_CODE || '').trim()
    const customerKey = `${custCode}::${branchCode}`
    if (order.ITEMS) {
      order.ITEMS.forEach((item) => {
        const targetInv = newLevel2Inventory.find(
          (inv) => String(inv.originalId) === String(item.ITEM_ID)
        )
        if (targetInv) {
          const l2Id = targetInv.id
          if (!finalMatrix[l2Id])
            finalMatrix[l2Id] = { level2: targetInv, customers: {} }
          if (!finalMatrix[l2Id].customers[customerKey]) {
            const custReq = newAllocationResults.find(
              (c) =>
                c.CUSTOMER_CODE === custCode &&
                c.BRANCH_CODE === branchCode &&
                c.MEISEI_HINMEI_CD === item.MEISEI_HINMEI_CD
            )
            finalMatrix[l2Id].customers[customerKey] = {
              CUSTOMER_CODE: custCode,
              BRANCH_CODE: branchCode,
              requested: custReq ? custReq.ORDER_QUANTITY : 0,
              allocated: 0
            }
          }
          finalMatrix[l2Id].customers[customerKey].allocated +=
            item.DELIVERY_QUANTITY
        }
      })
    }
  })

  return {
    inventory: newDetailsInventory,
    allocationResults: newAllocationResults,
    finalMatrix,
    orderScheduledList,
    level2Inventory: newLevel2Inventory,
    groupedById: newGroupedById,
    runBy: 'System',
    runAt: draftDetail.updated_at,
    seller_cd: inputData.seller_cd,
    delivery_date: inputData.delivery_date,
    rawInventory,
    rawCustomers,
    inputData
  }
}

// ─── Wizard state reducer ─────────────────────────────────────────────────────

const wizardInitialState = {
  step: 0,
  startDate: '',
  endDate: '',
  meiseiCode: '',
  draftName: '',
  showDialog: false,
  showAllocationSummary: false
}

const wizardReducer = (state, action) => {
  switch (action.type) {
    case 'OPEN':
      return { ...state, showDialog: true, step: 0 }
    case 'CLOSE':
      return { ...wizardInitialState }
    case 'SET_START_DATE':
      return { ...state, startDate: action.value }
    case 'SET_END_DATE':
      return { ...state, endDate: action.value }
    case 'SET_MEISEI_CODE':
      return { ...state, meiseiCode: action.value }
    case 'SET_DRAFT_NAME':
      return { ...state, draftName: action.value }
    case 'NEXT_STEP':
      return { ...state, step: state.step + 1 }
    case 'PREV_STEP':
      return { ...state, step: Math.max(0, state.step - 1) }
    case 'SHOW_SUMMARY':
      return { ...state, showAllocationSummary: true }
    default:
      return state
  }
}

// ─── CandidateTable (memoized — only re-renders when candidates change) ───────

const CandidateInventoryTable = memo(
  ({ items, selectedIds, onToggleAll, onToggle, t }) => (
    <div style={{ flex: 1 }}>
      <h4>{t('schedule.detail.inventoryInput')}</h4>
      <div
        className='table-container'
        style={{ maxHeight: 400, overflowY: 'auto' }}
      >
        <table className='custom-table compact-table ultra-compact'>
          <thead>
            <tr>
              <th>
                <input
                  type='checkbox'
                  checked={
                    items.length > 0 && selectedIds.size === items.length
                  }
                  onChange={onToggleAll}
                  className='fs-14'
                />
              </th>
              <th>Code</th>
              <th>{t('schedule.detail.grade')}</th>
              <th>{t('schedule.detail.size')}</th>
              <th>{t('schedule.detail.prod')}</th>
              <th style={{ textAlign: 'right' }}>
                {t('schedule.detail.price')}
              </th>
              <th style={{ textAlign: 'right' }}>{t('schedule.detail.qty')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={idx}>
                <td>
                  <input
                    type='checkbox'
                    checked={selectedIds.has(idx)}
                    onChange={() => onToggle(idx)}
                  />
                </td>
                <td>{item.MEISEI_HINMEI_CD}</td>
                <td>{item.GRADE}</td>
                <td>{item.SIZE}</td>
                <td>{item.PRODUCTION}</td>
                <td className='text-right pr-3'>{item.PRICE}</td>
                <td className='text-right pr-3'>{item.QUANTITY}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
)

const CandidateCustomerTable = memo(
  ({ items, selectedIds, onToggleAll, onToggle, t }) => (
    <div style={{ flex: 1 }}>
      <h4>{t('schedule.detail.orderInput')}</h4>
      <div
        className='table-container'
        style={{ maxHeight: 400, overflowY: 'auto' }}
      >
        <table className='custom-table compact-table ultra-compact'>
          <thead>
            <tr>
              <th>
                <input
                  type='checkbox'
                  checked={
                    items.length > 0 && selectedIds.size === items.length
                  }
                  onChange={onToggleAll}
                />
              </th>
              <th>{t('schedule.detail.customer')}</th>
              <th>{t('schedule.detail.branch')}</th>
              <th>Code</th>
              <th>{t('schedule.detail.size')}</th>
              <th style={{ textAlign: 'right' }}>
                {t('schedule.detail.price')}
              </th>
              <th style={{ textAlign: 'right' }}>{t('schedule.detail.qty')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((order, idx) => (
              <tr key={idx}>
                <td>
                  <input
                    type='checkbox'
                    checked={selectedIds.has(idx)}
                    onChange={() => onToggle(idx)}
                  />
                </td>
                <td>{order.customer_code || order.CUSTOMER_CODE}</td>
                <td>{order.branch_code || order.BRANCH_CODE}</td>
                <td>{order.MEISEI_HINMEI_CD}</td>
                <td>
                  {Array.isArray(order.SIZE) ? order.SIZE[0] : order.SIZE}
                </td>
                <td className='text-right pr-3'>{order.UNIT_PRICE}</td>
                <td className='text-right pr-3'>{order.ORDER_QUANTITY}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
)

const PageSchedule = () => {
  const { t } = useTranslation()
  const { setToolbar, clearToolbar } = useTopToolbar()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [schedules, setSchedules] = useState([])
  const [selectedSchedule, setSelectedSchedule] = useState(null)
  const [showRunningProgress, setShowRunningProgress] = useState(false)
  const [selectedAllocationCode, setSelectedAllocationCode] = useState('')
  const [selectedInventory, setSelectedInventory] = useState({})
  const [mapping, setMapping] = useState({})
  const [allocationResults, setAllocationResults] = useState([])
  const [viewCode, setViewCode] = useState(null)
  const [viewMode, setViewMode] = useState('allocation')
  const [apiLoading, setApiLoading] = useState(false)
  const [candidates, setCandidates] = useState({ inventory: [], customers: [] })
  const [selectedInventoryIds, setSelectedInventoryIds] = useState(new Set())
  const [selectedCustomerIds, setSelectedCustomerIds] = useState(new Set())
  const [detailsInventory, setDetailsInventory] = useState([])
  const [level2Inventory, setLevel2Inventory] = useState([])
  const [selectedRightInvId, setSelectedRightInvId] = useState(null)

  const [wizard, dispatchWizard] = useReducer(wizardReducer, wizardInitialState)

  const activeTab = searchParams.get('activeTab') || null

  const setActiveTab = useCallback(
    (tab) => {
      if (tab) setSearchParams({ activeTab: tab })
      else setSearchParams({})
    },
    [setSearchParams]
  )

  const groupedById = useMemo(
    () =>
      detailsInventory.reduce((acc, det) => {
        acc[det.id] = allocationResults.filter(
          (r) => String(r.MEISEI_HINMEI_CD) === String(det.MEISEI_HINMEI_CD)
        )
        return acc
      }, {}),
    [detailsInventory, allocationResults]
  )

  const displayDraft = useMemo(() => {
    if (selectedSchedule?.draftData) return selectedSchedule.draftData
    return {
      inventory: detailsInventory,
      allocationResults,
      groupedById,
      orderScheduledList: [],
      runBy: null,
      runAt: null
    }
  }, [selectedSchedule, detailsInventory, allocationResults, groupedById])

  const draftOrders = useMemo(
    () => displayDraft?.allocationResults || [],
    [displayDraft]
  )

  useEffect(() => {
    try {
      setToolbar({ left: <span style={{ fontWeight: 700 }}>荷割画面</span> })
      return () => clearToolbar()
    } catch (_) {}
  }, [setToolbar, clearToolbar])

  useEffect(() => {
    const inv = displayDraft?.inventory
    if (selectedSchedule && inv?.length > 0) {
      const first = inv[0]
      const key =
        first.sku || first.id || Object.keys(displayDraft.groupedById || {})[0]
      setSelectedRightInvId(key)
      setViewCode(key)
    } else {
      setSelectedRightInvId(null)
      setViewCode(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchedule?.id, displayDraft])

  useEffect(() => {
    if (wizard.step === 2) {
      const sel = Object.keys(selectedInventory)
      if (sel.length > 0) setViewCode(sel[0])
      else if (allocationResults.length > 0)
        setViewCode(String(allocationResults[0].MEISEI_HINMEI_CD))
      else setViewCode(null)
    }
  }, [wizard.step, selectedInventory, allocationResults])

  const handleToggleAllInventory = useCallback(
    (e) => {
      setSelectedInventoryIds(
        e.target.checked
          ? new Set(candidates.inventory.map((_, i) => i))
          : new Set()
      )
    },
    [candidates.inventory]
  )

  const handleToggleInventoryItem = useCallback((idx) => {
    setSelectedInventoryIds((prev) => {
      const next = new Set(prev)
      next.has(idx) ? next.delete(idx) : next.add(idx)
      return next
    })
  }, [])

  const handleToggleAllCustomers = useCallback(
    (e) => {
      setSelectedCustomerIds(
        e.target.checked
          ? new Set(candidates.customers.map((_, i) => i))
          : new Set()
      )
    },
    [candidates.customers]
  )

  const handleToggleCustomerItem = useCallback((idx) => {
    setSelectedCustomerIds((prev) => {
      const next = new Set(prev)
      next.has(idx) ? next.delete(idx) : next.add(idx)
      return next
    })
  }, [])

  const handleSelectSchedule = useCallback(
    async (sch) => {
      setActiveTab('new')
      setSelectedSchedule(sch)

      if (!sch.id) return
      try {
        const draftDetail = await getDraftById(sch.id)
        if (!draftDetail) return

        const builtData = buildDraftDataFromDetail(draftDetail)

        if (builtData.inputData?.distributionData) {
          const params = new URLSearchParams()
          params.set('draftId', sch.id)
          const popupData = {
            selectedNyukaNumbers: {},
            inventoryData: {},
            activeHinmeiTab: null,
            distributionData: builtData.inputData.distributionData,
            changeLog: builtData.inputData.changeLog || [],
            createdAt: draftDetail.created_at
          }
          builtData.rawInventory.forEach((item) => {
            const hinmeiCode = item.MEISEI_HINMEI_CD || item.meisei_hinmei_cd
            if (!popupData.inventoryData[hinmeiCode])
              popupData.inventoryData[hinmeiCode] = []
            popupData.inventoryData[hinmeiCode].push({
              niukabango: item.nyuka_number || item.ID || item.id,
              items: [
                {
                  hinmeiCode,
                  niukabango: item.nyuka_number || item.ID || item.id,
                  grade: item.GRADE || item.grade,
                  size: item.SIZE || item.size,
                  sales_date: item.DELIVERY_DATE || item.delivery_date,
                  arrival_shipping_at: item.arrival_shipping_at || null,
                  arrival_received_at: item.arrival_received_at || null,
                  loading_port_1: item.loading_port_1 || '',
                  loading_port_2: item.loading_port_2 || '',
                  reserve: item.reserve || '',
                  location: item.location || '',
                  branch_number: item.branch_number || '',
                  quantity: item.QUANTITY || item.quantity,
                  remain_quantity: item.remain_quantity || item.quantity,
                  school_quantity: item.school_quantity || 0,
                  hospital_quantity: item.hospital_quantity || 0,
                  distribution_result: item.distribution_result || []
                }
              ]
            })
            if (!popupData.selectedNyukaNumbers[hinmeiCode])
              popupData.selectedNyukaNumbers[hinmeiCode] = []
            popupData.selectedNyukaNumbers[hinmeiCode].push(
              item.nyuka_number || item.ID || item.id
            )
          })
          const payloadKey = `draft-${sch.id}`
          sessionStorage.setItem(payloadKey, JSON.stringify(popupData))
          params.set('payloadKey', payloadKey)
          navigate(`/allocation/schedule/right-panel?${params.toString()}`)
          return
        }

        const updater = (prev) =>
          prev.map((s) =>
            s.id === sch.id ? { ...s, draftData: builtData } : s
          )

        setSchedules(updater)
        setSelectedSchedule((prev) =>
          prev?.id === sch.id ? { ...prev, draftData: builtData } : prev
        )

        if (builtData.inventory.length > 0) {
          setViewCode(builtData.inventory[0].id)
          setViewMode(
            Object.keys(builtData.finalMatrix).length > 0
              ? 'allocation'
              : 'orders'
          )
        }
      } catch (error) {
        console.error('Failed to load draft details', error)
      }
    },
    [setActiveTab, navigate]
  )

  const handleLoadDraftFromJson = useCallback(async () => {
    try {
      const response = await fetch('/draft.json')
      const draftData = await response.json()
      const inputData = draftData.input_data
      const rawInventory = inputData.data.inventory
      const rawCustomers = inputData.data.customers || []

      const newLevel2Inventory = rawInventory.map((item, idx) => ({
        id: idx + 1,
        originalId: item.nyuka_number || idx + 1,
        parentId: `parent-${item.meisei_hinmei_cd}-${item.grade}-${item.size}-${item.production}`,
        MEISEI_HINMEI_CD: item.meisei_hinmei_cd,
        LABEL: `${item.meisei_hinmei_cd}-${idx + 1}`,
        QUANTITY: item.quantity,
        DELIVERY_DATE: item.delivery_date,
        PRICE: item.price
      }))

      const groupedInv = {}
      newLevel2Inventory.forEach((l2) => {
        if (!groupedInv[l2.parentId]) {
          groupedInv[l2.parentId] = {
            id: l2.parentId,
            MEISEI_HINMEI_CD: l2.MEISEI_HINMEI_CD,
            GRADE: '',
            SIZE: '',
            PRODUCTION: '',
            QUANTITY: 0
          }
        }
        groupedInv[l2.parentId].QUANTITY += l2.QUANTITY
      })
      const detailsInventory = Object.values(groupedInv)
      const allocationResults = rawCustomers.map((c, i) => ({
        _uiId: `cust-${i}`,
        CUSTOMER_CODE: c.customer_code || '',
        BRANCH_CODE: c.branch_code || '',
        MEISEI_HINMEI_CD: c.meisei_hinmei_cd || '',
        GRADE: Array.isArray(c.grade) ? c.grade[0] : c.grade || '',
        SIZE: Array.isArray(c.size) ? c.size[0] : c.size || '',
        PRODUCTION: Array.isArray(c.production)
          ? c.production[0]
          : c.production || '',
        ORDER_QUANTITY: c.order_quantity || 0,
        PRIORITY: 1,
        SATISFACTION_PERCENT: 100,
        AGENTLIST: [],
        UNIT_PRICE: c.price || 0
      }))

      const mockSchedule = {
        id: 'draft-json',
        name: draftData.name,
        date: inputData.delivery_date,
        draftData: {
          inventory: rawInventory,
          customers: rawCustomers,
          level2Inventory: newLevel2Inventory,
          detailsInventory,
          allocationResults,
          groupedById: {},
          finalMatrix: {}
        }
      }
      setSelectedSchedule(mockSchedule)
      setActiveTab('new')
    } catch (error) {
      console.error('Failed to load draft.json:', error)
    }
  }, [setActiveTab])

  const handleApplyProductAllocations = useCallback(
    async (allocs) => {
      if (!allocs?.length || !selectedSchedule) return
      const existing = selectedSchedule.draftData || {}
      const newDraft = {
        ...existing,
        inventory: Array.isArray(existing.inventory)
          ? [...existing.inventory]
          : [],
        level2Inventory: Array.isArray(existing.level2Inventory)
          ? [...existing.level2Inventory]
          : [],
        finalMatrix: existing.finalMatrix ? { ...existing.finalMatrix } : {},
        inventory_used: Array.isArray(existing.inventory_used)
          ? [...existing.inventory_used]
          : Array.isArray(existing.inventory)
            ? existing.inventory.map((_, i) => i)
            : []
      }

      allocs.forEach((a) => {
        let parent = newDraft.inventory.find(
          (d) => String(d.MEISEI_HINMEI_CD) === String(a.product)
        )
        if (!parent) {
          parent = {
            id: `inv-${a.product}`,
            MEISEI_HINMEI_CD: a.product,
            GRADE: '',
            SIZE: '',
            PRODUCTION: '',
            QUANTITY: 0
          }
          newDraft.inventory = [...newDraft.inventory, parent]
          if (
            newDraft.inventory_used?.length &&
            typeof newDraft.inventory_used[0] === 'number'
          ) {
            newDraft.inventory_used = [
              ...newDraft.inventory_used,
              newDraft.inventory.length - 1
            ]
          } else if (newDraft.inventory_used?.length) {
            newDraft.inventory_used = [...newDraft.inventory_used, parent.id]
          }
        }
        const level2Id = `alloc-${a.product}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
        const level2 = {
          id: level2Id,
          parentId: parent.id,
          MEISEI_HINMEI_CD: a.product,
          QUANTITY: Number(a.totalBoxes) || 0,
          PRICE: 0,
          DELIVERY_DATE: null
        }
        newDraft.level2Inventory.push(level2)
        newDraft.finalMatrix[level2Id] = { level2, customers: {} }
      })

      const updater = (s) =>
        s.id === selectedSchedule.id ? { ...s, draftData: newDraft } : s
      setSchedules((prev) => prev.map(updater))
      setSelectedSchedule((prev) =>
        prev ? { ...prev, draftData: newDraft } : prev
      )
    },
    [selectedSchedule]
  )

  const handleCreateDraftFromSelection = useCallback(
    async (payload) => {
      setApiLoading(true)
      try {
        const draft = await createDraftFromSelection(payload)
        const drafts = await getAllDrafts()
        const mappedDrafts = drafts?.length ? drafts.map(mapDraft) : []
        if (mappedDrafts.length) setSchedules(mappedDrafts)
        if (draft?.id) {
          const newDraft = mappedDrafts.find((d) => d.id === draft.id) || draft
          if (newDraft) {
            setSelectedSchedule(newDraft)
            setActiveTab('history')
          }
        }
      } catch (err) {
        console.error('Failed to create draft from selection', err)
        throw err
      } finally {
        setApiLoading(false)
      }
    },
    [setActiveTab]
  )

  const handleAllocatedSchedule = useCallback((id) => {
    setSchedules((prev) =>
      prev.map((s) => (s.id === id ? { ...s, status: 'allocated' } : s))
    )
  }, [])

  const handleRevertSchedule = useCallback((id) => {
    setSchedules((prev) =>
      prev.map((s) => (s.id === id ? { ...s, status: 'draft' } : s))
    )
  }, [])

  const handleNextStep = useCallback(async () => {
    if (wizard.step === 0) {
      setApiLoading(true)
      try {
        const data = await getCandidates({
          delivery_date: wizard.startDate,
          hic_start: wizard.meiseiCode,
          hic_end: wizard.meiseiCode,
          meisei_code: wizard.meiseiCode,
          seller_cd: '1145'
        })
        if (data.inventory) {
          data.inventory.forEach((item) => {
            if (item.PRICE == null)
              item.PRICE = Math.floor(Math.random() * 101 + 300)
          })
        }
        if (data.customers) {
          data.customers.forEach((item) => {
            if (item.UNIT_PRICE == null)
              item.UNIT_PRICE = Math.floor(Math.random() * 101 + 300)
          })
        }
        setCandidates(data)
        setSelectedInventoryIds(new Set(data.inventory.map((_, i) => i)))
        setSelectedCustomerIds(new Set(data.customers.map((_, i) => i)))
        dispatchWizard({ type: 'NEXT_STEP' })
      } catch (error) {
        console.error('Error fetching candidates:', error)
        alert('Failed to fetch candidates. Please try again.')
      } finally {
        setApiLoading(false)
      }
    } else if (wizard.step === 1) {
      setApiLoading(true)
      try {
        const payload = {
          name: wizard.draftName,
          delivery_date: wizard.startDate,
          hic_start: wizard.meiseiCode,
          hic_end: wizard.meiseiCode,
          meisei_code: wizard.meiseiCode,
          seller_cd: '1145',
          inventory: candidates.inventory.filter((_, i) =>
            selectedInventoryIds.has(i)
          ),
          customers: candidates.customers.filter((_, i) =>
            selectedCustomerIds.has(i)
          )
        }
        const draft = await createDraftFromSelection(payload)
        dispatchWizard({ type: 'CLOSE' })

        const drafts = await getAllDrafts()
        const mappedDrafts = drafts?.length ? drafts.map(mapDraft) : []
        if (mappedDrafts.length) setSchedules(mappedDrafts)

        const newSchedule = mappedDrafts.find((d) => d.id === draft.id)
        if (newSchedule) handleSelectSchedule(newSchedule)
      } catch (error) {
        console.error('Error creating draft:', error)
        alert('Failed to process allocation. Please try again.')
      } finally {
        setApiLoading(false)
      }
    } else {
      dispatchWizard({ type: 'NEXT_STEP' })
    }
  }, [
    wizard,
    candidates,
    selectedInventoryIds,
    selectedCustomerIds,
    handleSelectSchedule
  ])

  const handlePrevStep = useCallback(
    () => dispatchWizard({ type: 'PREV_STEP' }),
    []
  )
  const handleCloseDialog = useCallback(
    () => dispatchWizard({ type: 'CLOSE' }),
    []
  )
  const handleStartAllocation = useCallback(() => {
    setActiveTab('new')
    dispatchWizard({ type: 'OPEN' })
  }, [setActiveTab])

  const runAllocationForSchedule = useCallback(
    async (scheduleId) => {
      setApiLoading(true)
      try {
        const updatedDraft = await allocateProducts(scheduleId)
        setSchedules((prev) =>
          prev.map((s) => (s.id === scheduleId ? { ...s, ...updatedDraft } : s))
        )
        if (selectedSchedule?.id === scheduleId)
          await handleSelectSchedule(selectedSchedule)
      } catch (e) {
        console.error('Allocation failed', e)
      } finally {
        setApiLoading(false)
      }
    },
    [selectedSchedule, handleSelectSchedule]
  )

  const runAllAllocations = useCallback(async () => {
    setApiLoading(true)
    try {
      const results = await Promise.all(
        schedules.map((s) => allocateProducts(s.id))
      )
      setSchedules((prev) => {
        const next = [...prev]
        results.forEach((res) => {
          if (res?.id) {
            const idx = next.findIndex((s) => s.id === res.id)
            if (idx !== -1) next[idx] = { ...next[idx], ...res }
          }
        })
        return next
      })
      if (selectedSchedule) await handleSelectSchedule(selectedSchedule)
    } catch (e) {
      console.error('Run All failed', e)
    } finally {
      setApiLoading(false)
    }
  }, [schedules, selectedSchedule, handleSelectSchedule])

  const loadFromService = useCallback(async () => {
    setApiLoading(true)
    try {
      const date =
        selectedSchedule?.date ||
        wizard.startDate ||
        new Date().toISOString().slice(0, 10)
      const products = await getProductsInWarehouse({
        seller_cd: 'ABC',
        delivery_date: date,
        hic_start: '001',
        hic_end: '999'
      })
      if (products?.length) setDetailsInventory(products)
      const customers = await getCustomers({
        delivery_date: date,
        meisei_code: products?.[0]?.MEISEI_HINMEI_CD || ''
      })
      if (customers?.length) setAllocationResults(customers)
    } catch (e) {
      console.error('Load from service error', e)
    } finally {
      setApiLoading(false)
    }
  }, [selectedSchedule, wizard.startDate])

  const refreshData = useCallback(() => {
    setSchedules([])
    setAllocationResults([])
    setSelectedInventory({})
    setMapping({})
    setShowRunningProgress(false)
    setSelectedSchedule(null)
    setViewCode(null)
  }, [])

  const handleUpdateInventoryPrice = useCallback(
    (scheduleId, invId, newPrice) => {
      const updater = (s) => {
        if (s.id !== scheduleId) return s
        const draft = { ...s.draftData }
        const list = (draft.level2Inventory || []).map((l) =>
          l.id === invId ? { ...l, PRICE: newPrice } : l
        )
        return { ...s, draftData: { ...draft, level2Inventory: list } }
      }
      setSchedules((prev) => prev.map(updater))
      setSelectedSchedule((prev) =>
        prev?.id === scheduleId ? updater(prev) : prev
      )
    },
    []
  )

  const handleUpdateCustomerPrice = useCallback(
    (scheduleId, custUiId, newPrice) => {
      const updater = (s) => {
        if (s.id !== scheduleId) return s
        const draft = { ...s.draftData }
        const list = (draft.allocationResults || []).map((c) =>
          c._uiId === custUiId ? { ...c, UNIT_PRICE: newPrice } : c
        )
        const newGroupedById = {}
        ;(draft.inventory || []).forEach((det) => {
          newGroupedById[det.id] = list.filter(
            (r) => String(r.MEISEI_HINMEI_CD) === String(det.MEISEI_HINMEI_CD)
          )
        })
        return {
          ...s,
          draftData: {
            ...draft,
            allocationResults: list,
            groupedById: newGroupedById
          }
        }
      }
      setSchedules((prev) => prev.map(updater))
      setSelectedSchedule((prev) =>
        prev?.id === scheduleId ? updater(prev) : prev
      )
    },
    []
  )

  const handleSaveInput = useCallback(
    async (scheduleId) => {
      if (!selectedSchedule?.draftData) return
      setApiLoading(true)
      try {
        const draft = selectedSchedule.draftData
        const inventory = (draft.level2Inventory || []).map((item) => ({
          MEISEI_HINMEI_CD: item.MEISEI_HINMEI_CD,
          GRADE: (draft.inventory.find((d) => d.id === item.parentId) || {})
            .GRADE,
          SIZE: (draft.inventory.find((d) => d.id === item.parentId) || {})
            .SIZE,
          PRODUCTION: (
            draft.inventory.find((d) => d.id === item.parentId) || {}
          ).PRODUCTION,
          QUANTITY: Number(item.QUANTITY),
          PRICE: Number(item.PRICE),
          DELIVERY_DATE: item.DELIVERY_DATE
        }))
        const customers = (draft.allocationResults || []).map((item) => ({
          CUSTOMER_CODE: item.CUSTOMER_CODE,
          BRANCH_CODE: item.BRANCH_CODE,
          MEISEI_HINMEI_CD: item.MEISEI_HINMEI_CD,
          GRADE: item.GRADE,
          SIZE: item.SIZE,
          PRODUCTION: item.PRODUCTION,
          ORDER_QUANTITY: Number(item.ORDER_QUANTITY),
          PRIORITY: item.PRIORITY,
          SATISFACTION_PERCENT: item.SATISFACTION_PERCENT,
          AGENTLIST: item.AGENTLIST,
          UNIT_PRICE: Number(item.UNIT_PRICE)
        }))
        await updateDraftInput(scheduleId, {
          seller_cd: draft.seller_cd,
          delivery_date: draft.delivery_date,
          data: { inventory, customers }
        })
        alert('Input data saved successfully!')
      } catch (error) {
        console.error('Failed to save input data', error)
        alert('Failed to save input data. Please try again.')
      } finally {
        setApiLoading(false)
      }
    },
    [selectedSchedule]
  )

  const handleUpdateFinalAllocation = useCallback(
    (scheduleId, level2Id, custKey, newVal) => {
      const updater = (s) => {
        if (s.id !== scheduleId) return s
        const draft = { ...s.draftData }
        const fm = { ...(draft.finalMatrix || {}) }
        const l2List = draft.level2Inventory || level2Inventory
        const levelInfo = fm[level2Id]
          ? { ...fm[level2Id] }
          : {
              level2: l2List.find((l) => l.id === level2Id) || {},
              customers: {}
            }
        const customersMap = { ...levelInfo.customers }
        if (!customersMap[custKey]) {
          customersMap[custKey] = {
            CUSTOMER_CODE: custKey.split('::')[0],
            BRANCH_CODE: custKey.split('::')[1] || '',
            requested: 0,
            allocated: 0
          }
        }
        const available = Number(levelInfo.level2?.QUANTITY || 0)
        const otherAllocated = Object.entries(customersMap).reduce(
          (sum, [k, v]) =>
            k === custKey ? sum : sum + Number(v.allocated || 0),
          0
        )
        const desired = Number(isNaN(Number(newVal)) ? 0 : Number(newVal))
        const allocated = Math.max(
          0,
          Math.min(desired, Math.max(0, available - otherAllocated))
        )
        customersMap[custKey] = { ...customersMap[custKey], allocated }
        fm[level2Id] = { ...levelInfo, customers: customersMap }
        return { ...s, draftData: { ...draft, finalMatrix: fm } }
      }
      setSchedules((prev) => prev.map(updater))
      setSelectedSchedule((prev) =>
        prev?.id === scheduleId ? updater(prev) : prev
      )
    },
    [level2Inventory]
  )

  return (
    <div className='page-schedule-container'>
      {!activeTab ? (
        <div className='schedule-home'>
          <div className='schedule-home-actions'>
            <button
              type='button'
              className='schedule-action-card schedule-action-card--new'
              onClick={() => setActiveTab('new')}
            >
              <span className='schedule-action-card__icon' aria-hidden='true'>
                <img src={newIcon} alt='' />
              </span>
              <span className='schedule-action-card__title text-6xl'>
                荷割する
              </span>
              <span className='schedule-action-card__subtitle text-2xl mt-4'>
                新しい荷割を作成する
              </span>
            </button>
            <button
              type='button'
              className='schedule-action-card schedule-action-card--history'
              onClick={() => setActiveTab('history')}
            >
              <span className='schedule-action-card__icon' aria-hidden='true'>
                <img src={historyIcon} alt='' />
              </span>
              <span className='schedule-action-card__title text-6xl'>
                過去の荷割
              </span>
              <span className='schedule-action-card__subtitle text-2xl mt-4'>
                以前の荷割を表示
              </span>
            </button>
          </div>
        </div>
      ) : activeTab === 'history' ? (
        <div style={{ paddingTop: '50px' }}>
          <button
            type='button'
            onClick={() => setActiveTab(null)}
            className='btn btn--ghost !text-2xl'
            aria-label={t('common.back', 'Back')}
            style={{
              position: 'absolute',
              top: '10px',
              left: '10px',
              padding: '8px 16px',
              background: 'transparent',
              color: '#5f6368',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            ← 戻る
          </button>
          <History
            handleAllocatedSchedule={handleAllocatedSchedule}
            handleRevertSchedule={handleRevertSchedule}
            isLoading={apiLoading}
            t={t}
            className='fs-14'
          />
        </div>
      ) : activeTab === 'new' ? (
        <div>
          <NewNiwari
            selectedSchedule={selectedSchedule}
            setActiveTab={setActiveTab}
            displayDraft={displayDraft}
            runAllocationForSchedule={runAllocationForSchedule}
            handleSaveInput={handleSaveInput}
            runAllAllocations={runAllAllocations}
            loadFromService={loadFromService}
            apiLoading={apiLoading}
            refreshData={refreshData}
            viewCode={viewCode}
            setViewCode={setViewCode}
            setSelectedRightInvId={setSelectedRightInvId}
            viewMode={viewMode}
            setViewMode={setViewMode}
            t={t}
            handleUpdateInventoryPrice={handleUpdateInventoryPrice}
            handleUpdateCustomerPrice={handleUpdateCustomerPrice}
            handleUpdateFinalAllocation={handleUpdateFinalAllocation}
            detailsInventory={detailsInventory}
            coverageData={coverageData}
            orderedDetails={orderedDetails}
            showRunningProgress={showRunningProgress}
            setShowRunningProgress={setShowRunningProgress}
            handleStartAllocation={handleStartAllocation}
            onApplyAllocations={handleApplyProductAllocations}
            onCreateDraft={handleCreateDraftFromSelection}
            draftOrders={draftOrders}
            level2Inventory={level2Inventory}
            draftData={null}
            onLoadDraftFromJson={handleLoadDraftFromJson}
          />
        </div>
      ) : null}

      {wizard.showDialog && (
        <div className='new-schedule-dialog'>
          {!wizard.showAllocationSummary ? (
            <>
              {wizard.step === 0 && (
                <div className='allocation-step'>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <h3>{t('schedule.wizard.step1')}</h3>
                    <div
                      className='so-header-right'
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <div
                        className='so-tool'
                        style={{
                          alignItems: 'center',
                          display: 'flex',
                          gap: 8
                        }}
                      >
                        <FaCalendarAlt />
                        <input
                          type='date'
                          value={wizard.startDate}
                          onChange={(e) =>
                            dispatchWizard({
                              type: 'SET_START_DATE',
                              value: e.target.value
                            })
                          }
                          style={{ height: 32 }}
                        />
                        <input
                          className='schedule-input-endate'
                          type='date'
                          value={wizard.endDate}
                          onChange={(e) =>
                            dispatchWizard({
                              type: 'SET_END_DATE',
                              value: e.target.value
                            })
                          }
                          style={{ height: 32 }}
                        />
                      </div>
                    </div>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <label style={{ marginRight: 8 }}>
                      {t('schedule.wizard.draftName')}:
                    </label>
                    <input
                      type='text'
                      placeholder={t('schedule.wizard.draftName')}
                      value={wizard.draftName}
                      onChange={(e) =>
                        dispatchWizard({
                          type: 'SET_DRAFT_NAME',
                          value: e.target.value
                        })
                      }
                      style={{ width: 200 }}
                    />
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <label style={{ marginRight: 8 }}>
                      {t('schedule.wizard.meiseiCode')}:
                    </label>
                    <input
                      type='text'
                      placeholder={t('schedule.wizard.meiseiCode')}
                      value={wizard.meiseiCode}
                      onChange={(e) =>
                        dispatchWizard({
                          type: 'SET_MEISEI_CODE',
                          value: e.target.value
                        })
                      }
                      style={{ width: 200 }}
                    />
                  </div>
                  <div className='schedule-date-button-container'>
                    <button onClick={handleCloseDialog}>
                      {t('schedule.wizard.cancel')}
                    </button>
                    <button
                      onClick={handleNextStep}
                      disabled={
                        !wizard.startDate || !wizard.meiseiCode || apiLoading
                      }
                    >
                      {apiLoading
                        ? t('schedule.wizard.loading')
                        : t('schedule.wizard.next')}
                    </button>
                  </div>
                </div>
              )}

              {wizard.step === 1 && (
                <div
                  className='allocation-step'
                  style={{ width: '90vw', maxWidth: 1200 }}
                >
                  <h3>{t('schedule.wizard.step2')}</h3>
                  <div style={{ display: 'flex', gap: 20 }}>
                    <CandidateInventoryTable
                      items={candidates.inventory}
                      selectedIds={selectedInventoryIds}
                      onToggleAll={handleToggleAllInventory}
                      onToggle={handleToggleInventoryItem}
                      t={t}
                    />
                    <CandidateCustomerTable
                      items={candidates.customers}
                      selectedIds={selectedCustomerIds}
                      onToggleAll={handleToggleAllCustomers}
                      onToggle={handleToggleCustomerItem}
                      t={t}
                    />
                  </div>
                  <div className='schedule-date-button-container'>
                    <button onClick={handlePrevStep}>
                      {t('schedule.wizard.back')}
                    </button>
                    <button onClick={handleNextStep} disabled={apiLoading}>
                      {apiLoading
                        ? t('schedule.wizard.creatingDraft')
                        : t('schedule.wizard.confirmCreate')}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className='allocation-summary'>
              <h3>Allocation Summary</h3>
              <div className='summary-box'>
                <div>
                  <strong>Code:</strong> {selectedAllocationCode || ''}
                </div>
                <div>
                  <strong>Period:</strong> {wizard.startDate} → {wizard.endDate}
                </div>
                <div style={{ marginTop: 8 }}>
                  {Object.values(selectedInventory).map((it) => (
                    <div
                      key={it.sku}
                      style={{ padding: 6, borderBottom: '1px solid #eee' }}
                    >
                      <div>
                        {it.name} ({it.sku})
                      </div>
                      <div>
                        Qty: {it.qty} — Category: {mapping[it.sku]}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className='schedule-date-button-container'>
                <button onClick={handleCloseDialog}>Close</button>
              </div>
            </div>
          )}
        </div>
      )}

      {showRunningProgress && (
        <div className='running-progress'>
          <h3>Running Schedule...</h3>
          <button onClick={() => setShowRunningProgress(false)}>Stop</button>
        </div>
      )}
    </div>
  )
}

export default PageSchedule
